import { css, SerializedStyles } from '@emotion/react'
import styled from '@emotion/styled'
import { FunctionComponent, PropsWithChildren } from 'react'
import { Colors, ColorUtil } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { Sizes } from '~/assets/style/tokens'
import { WithClassName } from '~/types/utils'
import { Icon, IconName } from './Icon'
import { Link } from './Link'

type AlertVariant = 'primary' | 'secondary' | 'tertiary' | 'warning' | 'danger'

const AlertVariantDefaultTextColor: { [key in AlertVariant]: SerializedStyles } = {
  primary: Fonts.colors.Ink,
  secondary: Fonts.colors.Ink,
  warning: Fonts.colors.Ink,
  danger: Fonts.colors.Ink,
  tertiary: Fonts.colors.Ink,
}

const AlertVariantBackground: { [key in AlertVariant]: SerializedStyles } = {
  primary: css`
    background: linear-gradient(90deg, ${ColorUtil.hexOpacity(Colors.King, 0.08)}, ${ColorUtil.hexOpacity(Colors.Pool, 0.08)});
  `,
  secondary: css`
    background: linear-gradient(90deg, ${ColorUtil.hexOpacity(Colors.Platinum, 0.08)}, ${ColorUtil.hexOpacity(Colors.Platinum, 0.08)});
  `,
  warning: css`
    background: ${Colors.Peach};
  `,
  danger: css`
    background: ${Colors.Mew};
  `,
  tertiary: css`
    background: ${Colors.Ghost};
  `,
}

const AlertVariantBorderColor: { [key in AlertVariant]: SerializedStyles } = {
  primary: css`
    background: linear-gradient(90deg, ${Colors.King} 13.95%, ${Colors.Pool} 107.15%);
  `,
  secondary: css`
    background: ${Colors.King};
  `,
  warning: css`
    background: ${Colors.Pumpkin};
  `,
  danger: css`
    background: ${Colors.Cherry};
  `,
  tertiary: css`
    background: ${Colors.Cobalt};
  `,
}

const AlertVariantStyles: { [key in AlertVariant]: SerializedStyles } = {
  primary: css`
    ${AlertVariantDefaultTextColor['primary']}
  `,
  secondary: css`
    ${AlertVariantDefaultTextColor['secondary']}
  `,
  warning: css`
    ${AlertVariantDefaultTextColor['warning']}
  `,
  danger: css`
    ${AlertVariantDefaultTextColor['danger']}
  `,
  tertiary: css`
    ${AlertVariantDefaultTextColor['tertiary']}
  `,
}

const Wrapper = styled.div<{ variant: AlertVariant }>`
  ${({ variant }) => AlertVariantStyles[variant]}
  ${Fonts.P2}

  position: relative;
  padding: 0.75rem;
  ${({ variant }) => AlertVariantBackground[variant]}
    
  /* Trick to make a gradient border working with border-radius */
  ::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 4px;
    padding: 1px;
    ${({ variant }) => AlertVariantBorderColor[variant]}
    mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    mask-composite: exclude;
    pointer-events: none;
  }
`
const Flex = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: start;
`

const AlertText = styled.div`
  display: flex;
  flex-direction: column;

  ${Link} {
    font-size: inherit;
    line-height: inherit;
  }
`

const AlertTitle = styled.div<{ lineHeight?: string }>`
  font-weight: 700;
  ${({ lineHeight }) =>
    lineHeight &&
    css`
    line-height: ${lineHeight};
  `}
`

const AlertDescription = styled.div<{ lineHeight?: string }>`
  ${({ lineHeight }) =>
    lineHeight &&
    css`
    line-height: ${lineHeight};
  `}
`

type Prop = {
  variant: AlertVariant
  title?: string
  iconName?: IconName
  iconSize?: `${number}rem` | `${number}em` | `${number}px`
}

export const BaseAlert: FunctionComponent<PropsWithChildren<Prop & WithClassName>> = ({ className, variant, title, iconName, iconSize = Sizes[22], children }) => {
  return (
    <Wrapper className={className} variant={variant}>
      <Flex>
        {iconName && <Icon name={iconName} width={iconSize} height={iconSize} />}
        <AlertText>
          <AlertTitle lineHeight={iconSize}>{title}</AlertTitle>
          <AlertDescription lineHeight={!title ? iconSize : undefined}>{children}</AlertDescription>
        </AlertText>
      </Flex>
    </Wrapper>
  )
}

export const Alert = styled(BaseAlert)``
